import { IKnmListFilterRaw } from 'APIPackage/KnmListModule/interfaces/IKnmListFilterRaw';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import {
  knmFilterFormApprovedSelectOptions,
  knmFilterFormBooleanSelectOptions,
  knmFilterFormNotifyLateSelectOptions,
  knmFilterFormSignedSelectOptions,
} from 'KnmListPackage/KnmListPanelModule/constants/knmListFilterOptions';
import { IKnmFilterForm } from 'KnmListPackage/KnmListPanelModule/interfaces/IKnmFilterFormSecondary';
import { ITerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';
import { IKnoAdminLevelSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnoAdminLevelSelectOption';
import { IChecklistFullnessTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IChecklistFullnessTypeSelectOption';
import { IFederalLawSelectOption } from 'CorePackage/CatalogsModule/interfaces/IFederalLawSelectOption';
import { ISupervisionTypeSelectOption } from 'CorePackage/CatalogsModule/hooks/useKnmSupervisionTypes';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { publishedStatusesSelectOptions } from 'NewsPackage/MainModule/constants/publishedStatuses';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IFederalLawErvk } from 'APIPackage/CatalogsModule/interfaces/IFederalLawErvk';
import { IRegisterTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IRegistryTypeSelectOption';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { IMspCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IMspCategorySelectOption';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { approveRequiredSelectOptions } from 'KnmErknmPackage/MainModule/constants/approveRequiredSelectOptions';

import { IKnmListFilter } from '../interfaces/IKnmListFilter';
import { IResponsibilityTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IResponsibilityTypeSelectOption';
import { IKnmSubjectTypeCommonSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmSubjectTypeCommonSelectOptions';

interface IMapRawToKnmFilter {
  raw: IKnmListFilterRaw;
  isErknm: boolean;
  territorialUnitsSelectOptions: ITerritorialUnitSelectOption[];
  federalLawsSelectOptions: Array<IFederalLawSelectOption | IDictionarySelectOption<IFederalLawErvk>>;
  knmTypesSelectOptions: IKnmTypeSelectOption[];
  organisationsSelectOptions: Array<IUserOrganisationSelectOption | IDictionaryKnoOrganizationSelectOption>;
  supervisionTypesSelectOptions: Array<ISupervisionTypeSelectOption>;
  registerTypesSelectOptions: IRegisterTypeSelectOption[];
  checklistFullnessTypesSelectOptions: IChecklistFullnessTypeSelectOption[];
  formKnoOrganizationAdminLevelsSelectOptions: IKnoAdminLevelSelectOption[];
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  knmStatusesSelectOptions: IKnmStatusSelectOption[];
  knmSubjectTypesCommonSelectOptions: IKnmSubjectTypeCommonSelectOption[];
  kindsSelectOptions: IDictionarySelectOption<IKnmKind>[] | undefined;
  mspCategoriesSelectOptions: IMspCategorySelectOption[] | undefined;
  responsibilityTypesSelectOptions: IResponsibilityTypeSelectOption[] | undefined;
}

export function mapRawToKnmFilter(data: IMapRawToKnmFilter): IKnmListFilter {
  const {
    raw,
    territorialUnitsSelectOptions,
    federalLawsSelectOptions,
    knmTypesSelectOptions,
    isErknm,
    organisationsSelectOptions,
    supervisionTypesSelectOptions,
    registerTypesSelectOptions,
    checklistFullnessTypesSelectOptions,
    formKnoOrganizationAdminLevelsSelectOptions,
    userProsecutorOrganisationsSelectOptions,
    knmStatusesSelectOptions,
    knmSubjectTypesCommonSelectOptions,
    kindsSelectOptions,
    mspCategoriesSelectOptions,
    responsibilityTypesSelectOptions,
  } = data;
  const prosecutorOrganizationsFiltered = userProsecutorOrganisationsSelectOptions.filter(selectOption =>
    raw?.prosecutorOrganizationIds?.some(
      prosecutorOrganizationId => String(prosecutorOrganizationId) === selectOption.id,
    ),
  );
  const statusesFiltered = knmStatusesSelectOptions.filter(selectOption => {
    return raw?.statusIds?.some(statusId => statusId === selectOption?.id);
  });
  const pubStatusesFiltered = publishedStatusesSelectOptions.filter(selectOption =>
    raw?.pubStatuses?.some(pubStatus => pubStatus === selectOption.value),
  );
  const subjectTypesFiltered = knmSubjectTypesCommonSelectOptions.filter(selectOption =>
    raw?.subjectTypeCodes?.some(subjectTypeCode => subjectTypeCode === selectOption.code),
  );
  const supervisionType = supervisionTypesSelectOptions.find(
    (supervisionTypeSelectOption: ISupervisionTypeSelectOption) =>
      supervisionTypeSelectOption?.id === raw?.supervisionTypeId,
  );

  return {
    name: raw?.name,
    years:
      raw?.years?.map(year => ({
        id: String(year),
        label: String(year),
        value: String(year),
      })) || [],
    domains: raw?.domainIds?.reduce((domains: IKnmFilterForm['domains'], domainId) => {
      const territorialUnitSelectOption = territorialUnitsSelectOptions.find(item => item.id === domainId);

      if (territorialUnitSelectOption) {
        const res = domains ?? [];

        res.push(territorialUnitSelectOption);

        return res;
      }

      return domains;
    }, undefined),
    includeDomainChild: raw?.includeDomainChild || false,
    ignoreKno: raw?.ignoreKno || false,
    federalLaws: (isErknm
      ? federalLawsSelectOptions.filter(federalLawSelectOption =>
          raw?.federalLawIds?.some(federalLawId => federalLawId === Number(federalLawSelectOption.id)),
        )
      : federalLawsSelectOptions.filter(federalLawSelectOption =>
          raw?.federalLawErvkUuids?.some(federalLawErvkUuid => federalLawErvkUuid === federalLawSelectOption.id),
        )) as IKnmListFilter['federalLaws'],
    knmTypes: knmTypesSelectOptions.filter(knmTypeSelectOption =>
      raw?.knmTypeIds?.some(knmTypeId => knmTypeId === knmTypeSelectOption.id),
    ),
    controllingOrganizations: organisationsSelectOptions?.filter(
      (userKnoOrganisationSelectOption: IUserOrganisationSelectOption | IDictionaryKnoOrganizationSelectOption) =>
        raw?.controllingOrganizationIds?.some(controllingOrganizationId =>
          isErknm && 'idStr' in userKnoOrganisationSelectOption
            ? Number(controllingOrganizationId) === Number(userKnoOrganisationSelectOption.idStr)
            : Number(controllingOrganizationId) === Number(userKnoOrganisationSelectOption.id),
        ),
    ),
    includeChildKno: raw?.includeChildKno || false,
    disapprove: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.disapprove),
    supervisionType,
    supervisionTypeId: isErknm ? supervisionType?.code : raw.supervisionTypeId,
    hasViolations: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.hasViolations),
    dataNotEqualEGRUL: knmFilterFormBooleanSelectOptions.find(
      selectOption => selectOption.value === raw?.dataNotEqualEGRUL,
    ),
    startDateBetween: raw?.startDateBetween,
    ...(raw?.startDate && {
      startDate: formatDate({ date: raw?.startDate, dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    ...(raw?.startDateBetween?.[0] && {
      startDateBetweenStart: formatDate({ date: raw?.startDateBetween?.[0], dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    ...(raw?.startDateBetween?.[1] && {
      startDateBetweenEnd: formatDate({ date: raw?.startDateBetween?.[1], dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    ...(raw?.approveDocRequestDate && {
      approveDocRequestDate: formatDate({ date: raw?.approveDocRequestDate, dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    ...(raw?.approveDocOrderDate && {
      approveDocOrderDate: formatDate({ date: raw?.approveDocOrderDate, dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    chlistFillingIndicationType: checklistFullnessTypesSelectOptions.find(
      selectOption => selectOption?.id === raw?.chlistFillingIndicationTypeId,
    ),
    subjectInn: raw?.subjectInn,
    knmAnnulled: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.knmAnnulled),
    createdByMe: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.createdByMe),
    subjectOrgName: raw?.subjectOrgName,
    regType: registerTypesSelectOptions.find(selectedOption => selectedOption.value === raw?.regType),
    planNum: raw?.planNum,
    approveDocOrderNum: raw?.approveDocOrderNum,
    approveDocRequestNum: raw?.approveDocRequestNum,
    objectOgrn: raw?.objectOgrn,
    prosecutorOrganizations: prosecutorOrganizationsFiltered.length ? prosecutorOrganizationsFiltered : undefined,
    dataSetNotInTime: knmFilterFormBooleanSelectOptions.find(
      selectOption => selectOption.value === raw?.dataSetNotInTime,
    ),
    deletedOnly: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.deletedOnly),
    deletedInclude: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.deletedInclude),
    legalBasisDocName: raw?.legalBasisDocName,
    orderDone: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.orderDone),
    recEmptyOrLate: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.recEmptyOrLate),
    regLate: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.regLate),
    resultEmpty: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.resultEmpty),
    resultEmptyOrLate: knmFilterFormBooleanSelectOptions.find(
      selectOption => selectOption.value === raw?.resultEmptyOrLate,
    ),
    appealed: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.appealed),
    noPunishment: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.noPunishment),
    approved: knmFilterFormApprovedSelectOptions.find(selectOption => selectOption.value === raw?.approved),
    statuses: statusesFiltered.length ? statusesFiltered : undefined,
    signed: knmFilterFormSignedSelectOptions.find(selectOption => selectOption.value === raw?.signed),
    pubStatuses: pubStatusesFiltered.length ? pubStatusesFiltered : undefined,
    subjectTypes: subjectTypesFiltered.length ? subjectTypesFiltered : undefined,
    notifyLate: knmFilterFormNotifyLateSelectOptions.find(selectOption => selectOption.value === raw?.notifyLate),
    admLevel: formKnoOrganizationAdminLevelsSelectOptions.find(selectOption => selectOption.id === raw?.admLevelErvkId),
    erpId: raw?.erpId,
    inspectorFullName: raw?.inspectorFullName,
    address: raw.address,
    ...(raw?.createPlanDate && {
      createPlanDate: formatDate({ date: raw?.createPlanDate, dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    ...(raw?.updatePlanDate && {
      updatePlanDate: formatDate({ date: raw?.updatePlanDate, dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    ...(isErknm && {
      kinds:
        kindsSelectOptions &&
        kindsSelectOptions.filter(kindsSelectOption => raw?.kinds?.some(kind => kind === kindsSelectOption.label)),
    }),
    mspCategory: mspCategoriesSelectOptions?.find(selectedOption => selectedOption.title === raw.mspCategory?.[0]),
    approveRequiredType: raw?.approveRequiredType
      ? approveRequiredSelectOptions?.find(item => raw?.approveRequiredType === item?.value)
      : undefined,
    approvedRequired: knmFilterFormBooleanSelectOptions?.find(item => item.value === raw.approvedRequired),
    requirementDetails: raw?.requirementDetails,
    dateRequirement: raw?.dateRequirement,
    assignmentNumber: raw?.assignmentNumber,
    assignmentDateBetween: raw?.assignmentDateBetween,
    ...(raw?.assignmentDate && {
      assignmentDate: formatDate({ date: raw?.assignmentDate, dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    ...(raw?.assignmentDateBetween?.[0] && {
      assignmentDateBetweenStart: formatDate({
        date: raw?.assignmentDateBetween?.[0],
        dateFormat: DATE_FORMAT_LONG_FNS,
      }),
    }),
    ...(raw?.assignmentDateBetween?.[1] && {
      assignmentDateBetweenEnd: formatDate({
        date: raw?.assignmentDateBetween?.[1],
        dateFormat: DATE_FORMAT_LONG_FNS,
      }),
    }),
    ...(isErknm && {
      responsibilityTypeCodes:
        responsibilityTypesSelectOptions &&
        responsibilityTypesSelectOptions.filter(option =>
          raw?.responsibilityTypeCodes?.some(type => type === option.id),
        ),
    }),
    amountOfPunishmentBetween: raw?.amountOfPunishmentBetween,
    ...(raw?.amountOfPunishmentBetween?.[0] && {
      amountOfPunishmentFrom: raw?.amountOfPunishmentBetween?.[0],
    }),
    ...(raw?.amountOfPunishmentBetween?.[1] && {
      amountOfPunishmentTo: raw?.amountOfPunishmentBetween?.[1],
    }),
  };
}
